import { arredondarParaMoeda, CodigoMoeda } from './arredondamento-moeda.util';

// =============================================================================
// CONVERSÃO DIRETA (modelo do backend `ConversorMoedasUtil.kt`)
// =============================================================================
//
// `converterDireto` espelha `ConversorMoedasUtil.kt`: conversão DIRETA par-a-par,
// sem moeda-pivô, arredondando por etapa (destino PYG = 0 casas; destino
// BRL/USD/ARS = 2 casas; sempre HALF_UP). Os pares ARS↔USD e ARS↔BRL são
// proibidos (lança `ConversaoNaoPermitidaError`), igual ao backend.
//
// Este é o ÚNICO conversor do módulo. Usado pelos componentes do Forma de
// Pagamento e no cálculo de `valorRecebidoNaConta` das telas de Venda, Compra
// de fornecedor e Devolução.

/** Cotações numéricas necessárias para a conversão direta. */
export interface CotacoesDiretas {
  /** dolarGuarani — PYG por 1 USD. */
  cotPYG: number;
  /** dolarReal — BRL por 1 USD. */
  cotBRL: number;
  /** realGuarani — PYG por 1 BRL. */
  cotRealGuarani: number;
  /** pesoGuarani — PYG por 1 ARS. */
  cotPesoGuarani: number;
}

/**
 * Erro dedicado para pares de conversão proibidos pelo backend
 * (ARS↔USD e ARS↔BRL). Espelha `ConversaoNaoPermitidaException` do Kotlin.
 *
 * `converterDireto` lança este erro nesses pares (paridade conceitual +
 * útil para testes). Os agregadores do Forma de Pagamento NÃO devem
 * deixar este erro propagar de getters usados no template — devem guardar
 * a chamada com `podeConverterEntre` antes (ver seção 6 da spec).
 */
export class ConversaoNaoPermitidaError extends Error {
  constructor(origem: string, destino: string) {
    super(`conversao_nao_permitida: ${origem}->${destino}`);
    this.name = 'ConversaoNaoPermitidaError';
    // Mantém a cadeia de protótipo correta ao transpilar para ES5.
    Object.setPrototypeOf(this, ConversaoNaoPermitidaError.prototype);
  }
}

/** Códigos ISO das 4 moedas operadas no PDV. */
type MoedaIso = 'PYG' | 'USD' | 'BRL' | 'ARS';

/**
 * Sanitiza o resultado de uma conversão e o arredonda para a menor unidade
 * da moeda de destino. Resultado NaN/não-finito vira 0 (espelha o efeito do
 * `validarCotacaoNaoZero` do backend de forma tolerante no front).
 */
function finalizarConversao(valor: number, destino: MoedaIso): number {
  if (!Number.isFinite(valor)) return 0;
  return arredondarParaMoeda(valor, destino as CodigoMoeda);
}

/**
 * Converte um valor entre duas moedas pelo modelo DIRETO par-a-par,
 * espelhando `ConversorMoedasUtil.kt` do backend. Sem moeda-pivô.
 *
 * Arredonda o resultado por etapa: destino PYG = 0 casas, demais = 2,
 * sempre HALF_UP (`arredondarParaMoeda`).
 *
 * Pares ARS↔USD e ARS↔BRL lançam `ConversaoNaoPermitidaError` (o backend
 * proíbe). Os agregadores do Forma de Pagamento devem evitar acionar esses
 * pares (ver seção 6 da spec).
 *
 * Proteção: cotação 0/null/undefined em divisão → retorna 0 (não lança,
 * exceto nos pares proibidos). Resultado NaN/não-finito → retorna 0.
 *
 * @param valor   valor numérico na moeda de origem
 * @param origem  moeda de origem (código ISO)
 * @param destino moeda de destino (código ISO)
 * @param cot     cotações numéricas disponíveis
 */
export function converterDireto(
  valor: number,
  origem: MoedaIso,
  destino: MoedaIso,
  cot: CotacoesDiretas,
): number {
  if (origem === destino) return finalizarConversao(valor || 0, destino);
  if (!valor || !Number.isFinite(valor)) return 0;

  const cotPYG = cot?.cotPYG || 0;
  const cotBRL = cot?.cotBRL || 0;
  const cotRealGuarani = cot?.cotRealGuarani || 0;
  const cotPesoGuarani = cot?.cotPesoGuarani || 0;

  switch (origem) {
    case 'USD':
      // # 2 USD→BRL | # 3 USD→PYG | # 4 USD→ARS (proibido)
      if (destino === 'BRL') return finalizarConversao(valor * cotBRL, 'BRL');
      if (destino === 'PYG') return finalizarConversao(valor * cotPYG, 'PYG');
      throw new ConversaoNaoPermitidaError('USD', 'ARS');

    case 'BRL':
      // # 5 BRL→USD | # 7 BRL→PYG | # 8 BRL→ARS (proibido)
      if (destino === 'USD') {
        if (cotBRL <= 0) return 0;
        return finalizarConversao(valor / cotBRL, 'USD');
      }
      if (destino === 'PYG') return finalizarConversao(valor * cotRealGuarani, 'PYG');
      throw new ConversaoNaoPermitidaError('BRL', 'ARS');

    case 'PYG':
      // # 9 PYG→USD | # 10 PYG→BRL | # 12 PYG→ARS
      if (destino === 'USD') {
        if (cotPYG <= 0) return 0;
        return finalizarConversao(valor / cotPYG, 'USD');
      }
      if (destino === 'BRL') {
        if (cotRealGuarani <= 0) return 0;
        return finalizarConversao(valor / cotRealGuarani, 'BRL');
      }
      // destino === 'ARS'
      if (cotPesoGuarani <= 0) return 0;
      return finalizarConversao(valor / cotPesoGuarani, 'ARS');

    case 'ARS':
      // # 13 ARS→USD (proibido) | # 14 ARS→BRL (proibido) | # 15 ARS→PYG
      if (destino === 'PYG') return finalizarConversao(valor * cotPesoGuarani, 'PYG');
      throw new ConversaoNaoPermitidaError('ARS', destino);

    default:
      return 0;
  }
}
